import { useState, useRef, Suspense } from "react";
import { Canvas, useFrame } from "@react-three/fiber";
import {
  Points,
  PointMaterial,
  MeshDistortMaterial,
  useTexture,
} from "@react-three/drei";
// @ts-ignore
import * as random from "maath/random/dist/maath-random.esm";

const Moon = () => {
  const moonRef = useRef<any>();

  // NASA textures are Public Domain.
  // Best Practice: Download these to your /public folder to avoid hotlinking.
  const [colorMap, displacementMap, normalMap] = useTexture([
    // Place your downloaded textures in /public/textures/
    "/textures/moon-color-map.jpg",
    "/textures/moon-color-map.jpg",
    "/textures/normal-map.jpg",
  ]);

  // Optional: Rotate the moon slowly so we see different craters
  useFrame((state, delta) => {
    if (moonRef.current) {
      moonRef.current.rotation.y += delta * 0.05; // Slower rotation
    }
  });

  return (
    // We increase the radius slightly and move it back further for depth
    <mesh
      ref={moonRef}
      position={[0.3, 0.5, -0.8]}
      rotation={[0, Math.PI / 2, 0]}
    >
      {/* We need high detail geometry (many segments) for displacement maps to work */}
      <sphereGeometry args={[0.2, 64, 64]} />

      {/* MeshDistortMaterial combines the realistic lighting of meshStandardMaterial 
        with subtle, dynamic animation.
      */}
      <MeshDistortMaterial
        map={colorMap} // The visual texture
        displacementMap={displacementMap} // The crater height map
        displacementScale={0.015} // How deep/high the craters are
        normalMap={normalMap} // Adds finer lighting detail without geometry
        normalScale={[0.1, 0.1]} // Keep normals subtle
        color="#f0f0f0" // Tint
        roughness={0.9} // Moons are not shiny
        metalness={0.0}
        // Distort settings (the "wobble")
        distort={0.1} // Amount of distortion (0 to 1)
        speed={1} // Speed of the animation
      />
    </mesh>
  );
};

// ... (StarBackground component remains unchanged except for the typo fix)

const StarBackground = (props: any) => {
  const ref: any = useRef();
  const [sphere] = useState(() =>
    random.inSphere(new Float32Array(5000), { radius: 1.25 }),
  );

  useFrame((state, delta) => {
    ref.current.rotation.x -= delta / 10;
    ref.current.rotation.y -= delta / 15;
  });

  return (
    <group rotation={[0, 0, Math.PI / 4]}>
      <Points ref={ref} positions={sphere} stride={3} frustumCulled {...props}>
        <PointMaterial
          transparent
          color="white"
          size={0.002}
          sizeAttenuation={true}
          depthWrite={false} // Typo fix: depthWrite instead of dethWrite
        />
      </Points>
    </group>
  );
};

export const StarsCanvas = () => (
  <div className="w-full h-full flex z-20">
    {/* Setting the background to black is crucial when using ambient lighting */}
    <Canvas camera={{ position: [0, 0, 1] }} style={{ background: "black" }}>
      <Suspense fallback={null}>
        {/*
          Directional Light is absolutely necessary for craters (displacementMaps)
          to cast shadows and look 3D.
        */}
        <ambientLight intensity={0.1} />{" "}
        {/* Keeps shadows from being pitch black */}
        <directionalLight
          position={[5, 3, 5]} // Light comes from the upper right
          intensity={2.5} // Brightness
        />
        <Moon />
        <StarBackground />
      </Suspense>
    </Canvas>
  </div>
);
